import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../../redux/authSlice";
import { fetchUserDetails } from "../../redux/userSlice";
import { forgetPassword, verifyResetToken, resetPassword } from "../../services/api";
import "../Login/login.css";
import { Container, Row, Col } from "react-bootstrap";
import fieldCover from "../../assets/covers/field.png";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { socketActions } from "../../redux/middleware/socketMiddleware";

const Login = ({ setIsRegister }) => {
  const [activeTab, setActiveTab] = useState("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Forgot password states
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotMethod, setForgotMethod] = useState("email");
  const [forgotPhone, setForgotPhone] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  // Loading states for forgot password actions
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, isAuthenticated, token, error } = useSelector((state) => state.auth);
  // const { details } = useSelector((state) => state.user || {});

  // Timer effect for resend OTP
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((currentTimer) => currentTimer - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Enhanced OTP input change handler
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input if value is entered
      if (value && index < 5) {
        const nextInput = document.querySelector(`input[name=otp-${index + 1}]`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle OTP input keydown for backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);

      if (index > 0) {
        const prevInput = document.querySelector(`input[name=otp-${index - 1}]`);
        if (prevInput) prevInput.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`);
      if (prevInput) prevInput.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      const nextInput = document.querySelector(`input[name=otp-${index + 1}]`);
      if (nextInput) nextInput.focus();
    }
  };

  // Reset all forget password related states
  const resetForgetPasswordStates = () => {
    setForgotPasswordStep(1);
    setForgotPasswordEmail("");
    setForgotMethod("email");
    setForgotPhone("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
    setResendTimer(0);
    setIsForgotPassword(false);
  };

  // Handle Request OTP
  const handleRequestOTP = async () => {
    if (forgotMethod === 'email') {
      if (!forgotPasswordEmail) {
        toast.error("Please enter your email address");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(forgotPasswordEmail)) {
        toast.error("Please enter a valid email address");
        return;
      }
    } else {
      if (!forgotPhone) {
        toast.error("Please enter your phone number");
        return;
      }
    }

    try {
      setIsRequestingOtp(true);
      setShowLoader(true);
      setResendTimer(30);
      if (forgotMethod === 'email') {
        await forgetPassword({ email: forgotPasswordEmail });
      } else {
        await forgetPassword({ phone: `+${forgotPhone}` });
      }
      setForgotPasswordStep(2);
      toast.success("OTP sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsRequestingOtp(false);
      setShowLoader(false);
    }
  };

  // Handle Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    try {
      setIsRequestingOtp(true);
      setShowLoader(true);
      if (forgotMethod === 'email') {
        await forgetPassword({ email: forgotPasswordEmail });
      } else {
        await forgetPassword({ phone: `+${forgotPhone}` });
      }
      setResendTimer(30);
      toast.success("OTP resent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsRequestingOtp(false);
      setShowLoader(false);
    }
  };

  // Handle OTP Verification
  const handleVerifyOTP = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setShowLoader(true);
      const payload = forgotMethod === 'email'
        ? { email: forgotPasswordEmail, otp: otpValue }
        : { phone: `+${forgotPhone}`, otp: otpValue };

      const response = await verifyResetToken(payload);

      if (response.data.success) {
        localStorage.setItem('resetToken', response.data.resetToken);
        toast.success("OTP verified successfully!");
        setForgotPasswordStep(3);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
      setShowLoader(false);
    }
  };

  // Handle Password Reset
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setIsResettingPassword(true);
      setShowLoader(true);
      const resetToken = localStorage.getItem('resetToken');
      if (!resetToken) {
        toast.error("Reset token not found. Please try the process again.");
        return;
      }

      await resetPassword({
        resetToken,
        newPassword: newPassword
      });

      localStorage.removeItem('resetToken');
      toast.success("Password reset successfully!");
      resetForgetPasswordStates();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setIsResettingPassword(false);
      setShowLoader(false);
    }
  };

  // Handle transition to register page
  const handleTransitionToRegister = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsRegister(true);
      setIsTransitioning(false);
    }, 500);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (activeTab === "email" && (!email || !password)) {
      toast.error("Please enter both email and password");
      return;
    }

    if (activeTab === "phone" && (!phone || !password)) {
      toast.error("Please enter both phone number and password");
      return;
    }

    setShowLoader(true);

    const emailorphone = activeTab === "email" ? email : `+${phone}`;
    const credentials = { emailorphone, password };

    try {
      const result = await dispatch(loginUser(credentials));
      console.log('Login result:', result);

      // Check if the action was fulfilled
      if (result.meta.requestStatus === 'fulfilled') {
        const responseData = result.payload;
        console.log('Login response data:', responseData);

        // Check if login was successful (has token and status is true)
        if (responseData.token && (responseData.status === true || responseData.statues === true)) {
          console.log("Login successful, received token");
          // Token will be stored via Redux authSlice using storage utility

          const userDetailsAction = await dispatch(fetchUserDetails());
          if (userDetailsAction.payload?.userDetail) {
            const { userDetail } = userDetailsAction.payload;

            localStorage.setItem('firstName', userDetail.firstName);
            localStorage.setItem('userId', userDetail._id);
            console.log("Saved user's details to localStorage");

            // Connect socket via Redux middleware (will be handled globally)
            console.log('SOCKET: Initiating connection via Redux middleware');
            dispatch(socketActions.connect(userDetail._id));

            toast.success("Login successful!");
            navigate("/home");
          }
        } else {
          // Login failed - the Redux slice will handle setting error state
          // The useEffect will handle showing the error toast
          setShowLoader(false);
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setShowLoader(false);
      toast.error("Login failed. Please try again later.");
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      // Token is now handled by Redux authSlice using storage utility
      dispatch(fetchUserDetails())
        .then(action => {
          if (action.payload?.userDetail?.firstName) {
            localStorage.setItem('firstName', action.payload.userDetail.firstName);
            localStorage.setItem('userId', action.payload.userDetail._id);
          }
        });
      navigate("/home");
      setShowLoader(false);
    }
  }, [isAuthenticated, token, navigate, dispatch]);

  // Watch for login errors
  useEffect(() => {
    if (error) {
      setShowLoader(false);
      toast.error(error.message || "Login failed. Please try again.");
    }
  }, [error]);

  useEffect(() => {
    if (!isForgotPassword) {
      resetForgetPasswordStates();
    }
  }, [isForgotPassword]);

  // Render forgot password steps
  const renderForgotPasswordContent = () => {
    switch (forgotPasswordStep) {
      case 1:
        return (
          <div className="forgot-password-container">
            <div
              className="back-card"
              onClick={resetForgetPasswordStates}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: 8, marginBottom: 12
              }}
            >
              <span style={{ fontSize: 18 }}>←</span> <span>Back</span>
            </div>
            <h2>{forgotMethod === 'email' ? 'Enter Your Email' : 'Enter Your Phone'}</h2>
            <p>
              {forgotMethod === 'email'
                ? "Enter your email address and we'll send you an OTP to reset your password."
                : "Enter your phone number and we'll send you an OTP to reset your password."}
            </p>
            {forgotMethod === 'email' ? (
              <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="Enter your email"
                className="email-input otp-request"
                style={{ width: '100%' }}
                disabled={isRequestingOtp}
              />
            ) : (
              <div style={{ width: '100%' }}>
                <PhoneInput
                  className="email-input"
                  country={"pk"}
                  value={forgotPhone}
                  onChange={setForgotPhone}
                  placeholder="Enter phone number"
                  containerStyle={{ width: '100%' }}
                  inputStyle={{ width: '100%', height: '44px' }}
                  buttonStyle={{ borderTopLeftRadius: 6, borderBottomLeftRadius: 6 }}
                  disabled={isRequestingOtp}
                />
              </div>
            )}
            <button
              className="request-otp-btn"
              onClick={handleRequestOTP}
              disabled={isRequestingOtp}
            >
              {isRequestingOtp ? 'Requesting...' : 'Request OTP'}
            </button>
          </div>
        );

      case 2:
        return (
          <div className="otp-container">
            <h2>Enter OTP</h2>
            <p>
              6 digit code has been sent to {forgotMethod === 'email' ? forgotPasswordEmail : `+${forgotPhone}`}
            </p>
            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  name={`otp-${index}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="otp-input"
                  autoComplete="off"
                  disabled={isVerifyingOtp}
                />
              ))}
            </div>
            <p className="resend-text">
              Didn't receive the code? {resendTimer > 0 ? (
                <span className="timer">Resend OTP in {resendTimer}s</span>
              ) : (
                <span className="resend-link" onClick={handleResendOTP}>Resend OTP</span>
              )}
            </p>
            <button
              className="verify-btn"
              onClick={handleVerifyOTP}
              disabled={isVerifyingOtp}
            >
              {isVerifyingOtp ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        );

      case 3:
        return (
          <div className="new-password-container">
            <h2>Enter New Password</h2>
            <p>Enter new password and confirm password</p>
            <div className="password-inputs">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New Password"
                className="password-input"
                disabled={isResettingPassword}
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                className="password-input"
                disabled={isResettingPassword}
              />
            </div>
            <p className="password-hint">Hint: Password must contain Special Characters and uppercase letters</p>
            <button
              className="save-password-btn"
              onClick={handleResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? 'Saving...' : 'Save Password'}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  if (isForgotPassword) {
    return (
      <>
        <Backdrop
          sx={{
            color: '#37b000',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.2)'
          }}
          open={showLoader || isTransitioning}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Container fluid className="login-page-container">
          <Row className="login-row">
            <Col lg={6} className="login-left-column">
              <div className="login-form-content">
                <div className="login-branding">
                  <h1 className="goplay-logo">GoPlay</h1>
                  <p className="portal-title">Goplay Sports Facility Management Portal</p>
                </div>
                <div className="login-form-wrapper forgot-password-form">
                  {renderForgotPasswordContent()}
                </div>
              </div>
            </Col>
            <Col lg={6} className="cover-column">
              <div className="cover-image-container">
                <img src={fieldCover} alt="Sports field" className="cover-image" />
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Backdrop
        sx={{
          color: '#37b000',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.2)'
        }}
        open={showLoader || isTransitioning}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* <Container fluid className="login-page-container"> */}
      <Container fluid className="login-page-container">
        <Row className="login-row">
          <Col lg={7} className="login-left-column">
            <div className="login-form-content">
              <div className="login-branding">
                <h1 className="goplay-logo">GoPlay</h1>
                <p className="portal-title">Goplay Sports Facility Management Portal</p>
              </div>
              <div className="nav-tabs">
                <span
                  className="nav-tab active"
                >
                  Login
                </span>
                <span
                  className="nav-tab"
                  onClick={handleTransitionToRegister}
                >
                  Register
                </span>
              </div>
              <div className="login-tabs">
                <button
                  type="button"
                  className={`login-tab ${activeTab === "phone" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("phone");
                    setEmail("");
                    setPassword("");
                  }}
                >
                  Phone Number
                </button>
                <button
                  type="button"
                  className={`login-tab ${activeTab === "email" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("email");
                    setPhone("");
                    setPassword("");
                  }}
                >
                  Email
                </button>
              </div>
              <form className="login-form-wrapper" onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">{activeTab === "phone" ? "Phone Number*" : "Email address*"}</label>
                  <div className="input-wrapper">
                    {activeTab === "phone" ? (
                      <PhoneInput
                        className="form-input phone-input"
                        country={"pk"}
                        value={phone}
                        onChange={setPhone}
                        placeholder="Enter phone number"
                        required
                        disabled={loading}
                      />
                    ) : (
                      <>
                        {/* <FaEnvelope className="input-icon" /> */}
                        <input
                          className="form-input"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                          disabled={loading}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Password*</label>
                  <div className="input-wrapper">
                    {/* <FaLock className="input-icon" /> */}
                    <input
                      className="form-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <p className="forgot-password-link" onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setForgotMethod(activeTab === 'phone' ? 'phone' : 'email');
                    if (activeTab === 'phone') {
                      setForgotPhone(phone);
                    } else {
                      setForgotPasswordEmail(email);
                    }
                    setIsForgotPassword(true);
                    setIsTransitioning(false);
                  }, 300);
                }}>
                  Forgot Password?
                </p>
                <button
                  type="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
                <p className="switch-link">
                  Don't have an account?{" "}
                  <span onClick={handleTransitionToRegister} className="link-text">
                    create account
                  </span>
                </p>
              </form>
              <p className="copyright-text">© GoPlay — All rights reserved.</p>
            </div>
          </Col>
          <Col lg={5} className="cover-column">
            <div className="cover-image-container">
              <img src={fieldCover} alt="Sports field" className="cover-image" />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Login;